import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import CircularProgress from '@mui/material/CircularProgress';
import { Link } from 'react-router-dom';

const drawerWidth = 280;

const AI_MODELS = [
  { id: 'gpt-4', name: 'GPT-4', provider: 'OpenAI' },
  { id: 'gpt-3.5', name: 'GPT-3.5', provider: 'OpenAI' },
  { id: 'claude-3', name: 'Claude 3', provider: 'Anthropic' },
  { id: 'gemini-pro', name: 'Gemini Pro', provider: 'Google' },
];

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${month}/${day} ${hours}:${minutes}`;
};

const formatTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
};

const ChatInterface = () => {
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const savedConversations = localStorage.getItem('conversations');
    if (savedConversations) {
      const parsed = JSON.parse(savedConversations);
      setConversations(parsed);
      if (parsed.length > 0) {
        setCurrentConversation(parsed[0]);
      }
    }
  }, []);

  const saveConversations = (newConversations) => {
    localStorage.setItem('conversations', JSON.stringify(newConversations));
    setConversations(newConversations);
  };

  const handleNewChat = () => {
    const newConversation = {
      id: Date.now().toString(),
      title: '新しい会話',
      messages: [{
        role: 'assistant',
        content: 'こんにちは！新しい会話を始めましょう。どのようなお手伝いができますか？',
        timestamp: new Date().toISOString()
      }],
      model: AI_MODELS[0],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const newConversations = [newConversation, ...conversations];
    saveConversations(newConversations);
    setCurrentConversation(newConversation);
    setInput('');
  };

  const handleDeleteConversation = (e, conversationId) => {
    e.stopPropagation();
    const newConversations = conversations.filter(c => c.id !== conversationId);
    saveConversations(newConversations);
    if (currentConversation?.id === conversationId) {
      setCurrentConversation(newConversations[0] || null);
    }
  };

  const handleSelectConversation = (conversation) => {
    setCurrentConversation(conversation);
    setInput('');
  };

  const handleSend = async () => {
    if (!input.trim() || !currentConversation) return;

    const userMessage = {
      role: 'user',
      content: input,
      timestamp: new Date().toISOString()
    };

    const updatedConversation = {
      ...currentConversation,
      messages: [...currentConversation.messages, userMessage],
      updatedAt: new Date().toISOString()
    };

    const newConversations = conversations.map(c =>
      c.id === currentConversation.id ? updatedConversation : c
    );
    saveConversations(newConversations);
    setCurrentConversation(updatedConversation);
    setInput('');
    setIsLoading(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      const assistantMessage = {
        role: 'assistant',
        content: `[${currentConversation.model.name}] これはデモ応答です。実際の${currentConversation.model.provider} APIとの統合が必要です。`,
        timestamp: new Date().toISOString()
      };

      const finalConversation = {
        ...updatedConversation,
        messages: [...updatedConversation.messages, assistantMessage],
        updatedAt: new Date().toISOString()
      };

      const finalConversations = conversations.map(c =>
        c.id === currentConversation.id ? finalConversation : c
      );
      saveConversations(finalConversations);
      setCurrentConversation(finalConversation);
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: 'エラーが発生しました。もう一度お試しください。',
        timestamp: new Date().toISOString()
      };
      const errorConversation = {
        ...updatedConversation,
        messages: [...updatedConversation.messages, errorMessage],
        updatedAt: new Date().toISOString()
      };
      const errorConversations = conversations.map(c =>
        c.id === currentConversation.id ? errorConversation : c
      );
      saveConversations(errorConversations);
      setCurrentConversation(errorConversation);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModelChange = (event) => {
    if (!currentConversation) return;
    
    const newModel = AI_MODELS.find(model => model.id === event.target.value);
    const updatedConversation = {
      ...currentConversation,
      model: newModel,
      updatedAt: new Date().toISOString()
    };

    const newConversations = conversations.map(c =>
      c.id === currentConversation.id ? updatedConversation : c
    );
    saveConversations(newConversations);
    setCurrentConversation(updatedConversation);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const generateTitle = (content) => {
    return content.slice(0, 20) + (content.length > 20 ? '...' : '');
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: 'grey.100' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            bgcolor: 'grey.900',
            color: 'white',
          },
        }}
      >
        <Box sx={{textAlign:"center",mt:1}}>
        <Typography variant="h6" sx={{ mr: 4 }}>
            <Link to="/" style={{textDecoration:"none",color: "#000000"}}><span className="App-logo d-none d-sm-inline-block" id="logo" style={{fontSize:"18px",cursor:"pointer",color:"#FFFFFF"}}><img src='/img/logo_new_footer.png' height={36} /></span></Link>
        </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleNewChat}
            sx={{ 
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.4)',
                bgcolor: 'rgba(255, 255, 255, 0.08)',
              }
            }}
          >
            新しいチャット
          </Button>
        </Box>
        <List sx={{ overflow: 'auto', flexGrow: 1 }}>
          {conversations.map((conversation) => (
            <ListItem
              key={conversation.id}
              disablePadding
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  onClick={(e) => handleDeleteConversation(e, conversation.id)}
                  sx={{ 
                    color: 'grey.500',
                    '&:hover': { color: 'white' }
                  }}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              }
            >
              <ListItemButton
                selected={currentConversation?.id === conversation.id}
                onClick={() => handleSelectConversation(conversation)}
                sx={{
                  '&.Mui-selected': {
                    bgcolor: 'grey.800',
                    '&:hover': {
                      bgcolor: 'grey.800',
                    },
                  },
                }}
              >
                <ListItemText
                  primary={generateTitle(conversation.messages[1]?.content || conversation.title)}
                  secondary={formatDate(conversation.updatedAt)}
                  primaryTypographyProps={{ 
                    sx: { color: 'white' }
                  }}
                  secondaryTypographyProps={{ 
                    sx: { color: 'grey.400' }
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {currentConversation ? (
          <>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 0 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>AIモデル</InputLabel>
                <Select
                  value={currentConversation.model.id}
                  onChange={handleModelChange}
                  label="AIモデル"
                >
                  {AI_MODELS.map((model) => (
                    <MenuItem key={model.id} value={model.id}>
                      {model.name} ({model.provider})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Paper>

            <Box sx={{ flexGrow: 1, p: 3, overflow: 'auto' }}>
              <Box sx={{ maxWidth: '800px', mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {currentConversation.messages.map((message, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      flexDirection: message.role === 'user' ? 'row-reverse' : 'row',
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: message.role === 'user' ? 'primary.main' : 'grey.900',
                        color: 'white',
                      }}
                    >
                      {message.role === 'user' ? <PersonIcon /> : <SmartToyIcon />}
                    </Box>
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        maxWidth: '70%',
                        bgcolor: message.role === 'user' ? 'primary.main' : 'background.paper',
                        color: message.role === 'user' ? 'white' : 'text.primary',
                      }}
                    >
                      <Typography variant="body1">{message.content}</Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
                        {formatTime(message.timestamp)}
                      </Typography>
                    </Paper>
                  </Box>
                ))}
              </Box>
            </Box>

            <Paper 
              elevation={3} 
              sx={{ 
                p: 2, 
                borderRadius: 0,
                borderTop: 1,
                borderColor: 'divider'
              }}
            >
              <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
                <Box sx={{ position: 'relative' }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={1}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="メッセージを入力..."
                    disabled={isLoading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        pr: 7,
                      },
                    }}
                  />
                  <IconButton
                    onClick={handleSend}
                    disabled={isLoading || !input.trim()}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                    }}
                  >
                    {isLoading ? <CircularProgress size={24} /> : <SendIcon />}
                  </IconButton>
                </Box>
                {currentConversation && (
                  <Typography variant="caption" align="center" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                    {currentConversation.model.name} by {currentConversation.model.provider}
                  </Typography>
                )}
              </Box>
            </Paper>
          </>
        ) : (
          <Box 
            sx={{ 
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary'
            }}
          >
            <Typography>
              会話を選択するか、新しい会話を開始してください
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChatInterface;